<script setup>
    import { reactive, ref, onMounted, watch } from 'vue'
    import NavigationBar from '../components/NavigationBar.vue'
    import HeaderComponent from '../components/HeaderComponent.vue'
    import CallApi from '../utils/callApi.js'

    // 推奨設定(デフォルト値)
    const DEFAULT_SETTINGS = {
        faceThreshold: 0.73,
        livenessThreshold: 0.75,
        maskThreshold: 0.5
    }

    const authSettings = reactive({
        // 推奨設定を使用するか
        useRecommended: false,
        // 顔認証閾値(通常)
        faceThreshold: DEFAULT_SETTINGS.faceThreshold,
        // ライブネス閾値
        livenessThreshold: DEFAULT_SETTINGS.livenessThreshold,
        // マスク時認証閾値
        maskThreshold: DEFAULT_SETTINGS.maskThreshold
    })

    const isLoading = ref(false) // ローディング画面表示フラグ
    const loadingMessage = ref('Loading...')

    // 認証設定を取得する
    const getAuthSettings = async() => {
        try {
            const data = await CallApi.getAxiosAuthThreshold()
            authSettings.faceThreshold = data?.faceauth_threshold ?? DEFAULT_SETTINGS.faceThreshold
            authSettings.livenessThreshold = data?.fas_threshold ?? DEFAULT_SETTINGS.livenessThreshold
            authSettings.maskThreshold = data?.mask_threshold ?? DEFAULT_SETTINGS.maskThreshold
        } catch (error) {
            console.error('認証設定取得 通信エラー:', error)
        }
    }

    onMounted(() => {
        getAuthSettings()
    })

    // デフォルト値に戻す
    const resetToDefault = () => {
        authSettings.faceThreshold = DEFAULT_SETTINGS.faceThreshold
        authSettings.livenessThreshold = DEFAULT_SETTINGS.livenessThreshold
        authSettings.maskThreshold = DEFAULT_SETTINGS.maskThreshold
    }

    // スライダーの塗りつぶし位置(0.40〜0.99に対する割合)をCSS変数で渡す
    const sliderFillStyle = (value) => {
        const percent = ((Number(value) - 0.40) / (0.99 - 0.40)) * 100
        return { '--fill': `${Math.min(100, Math.max(0, percent))}%` }
    }

    // 推奨設定を使用する場合、閾値をデフォルト値に揃えて編集不可にする
    watch(() => authSettings.useRecommended, (useRecommended) => {
        if (useRecommended) {
            resetToDefault()
        }
    })

    // 変更を保存する
    const saveAuthSettings = async() => {
        try {
            isLoading.value = true
            loadingMessage.value = '保存中...'
            await CallApi.patchAxiosAuthThreshold({
                faceauth_threshold: authSettings.faceThreshold,
                fas_threshold: authSettings.livenessThreshold,
                mask_threshold: authSettings.maskThreshold
            })
            alert('設定を保存しました')
        } catch (error) {
            console.error('認証設定保存 通信エラー:', error)
            alert('設定の保存に失敗しました。')
        } finally {
            isLoading.value = false
        }
    }

    // デフォルト値に戻してDBに保存する
    const resetAndSaveAuthSettings = async() => {
        resetToDefault()
        await saveAuthSettings()
    }
</script>
<template>
    <HeaderComponent></HeaderComponent>
    <NavigationBar></NavigationBar>
    <main>
        <div class="mainView">
            <div>
                <div class="titleArea">
                    <p class="title">認証設定</p>
                    <div class="titleTextArea">
                        <p class="titleText">顔認証に関する各種設定を行います。閾値の調整により、認証精度やセキュリティのバランスを変更できます。</p>
                    </div>
                </div>
                <div class="recommendedBanner">
                    <div class="bannerText">
                        <img src="../assets/authsetting_info.png" class="infoIcon" alt="">
                        <span>推奨設定を使用すると、一般的な環境で最適な精度となる値が自動で設定されます。</span>
                    </div>
                    <div class="bannerSwitchArea">
                        <span class="bannerSwitchLabel">推奨設定を使用</span>
                        <label class="toggleSwitch">
                            <input type="checkbox" v-model="authSettings.useRecommended">
                            <span class="toggleTrack"><span class="toggleThumb"></span></span>
                        </label>
                        <span class="helpIcon" title="ONにすると、個別の調整はできなくなります。">?</span>
                    </div>
                </div>

                <div class="settingsCard">
                    <div class="settingIconBadge">
                        <img src="../assets/dashboard_registNum.png" alt="">
                    </div>
                    <div class="settingLabel">
                        <span>顔認証閾値（通常）</span>
                        <span class="helpIcon" title="通常時(マスクなし)の顔認証で、本人と判定するための類似度スコアの閾値です。値を高くすると、他人を本人として認証する可能性が低下しますが、本人を認証できない可能性が高くなります。">?</span>
                    </div>
                    <input
                        type="number"
                        class="settingNumberInput"
                        v-model.number="authSettings.faceThreshold"
                        :disabled="authSettings.useRecommended"
                        min="0.40"
                        max="0.99"
                        step="0.01"
                    >
                    <div class="settingSliderArea">
                        <input
                            type="range"
                            class="settingSlider"
                            v-model.number="authSettings.faceThreshold"
                            :style="sliderFillStyle(authSettings.faceThreshold)"
                            :disabled="authSettings.useRecommended"
                            min="0.40"
                            max="0.99"
                            step="0.01"
                        >
                        <div class="settingRangeLabels">
                            <span>0.40</span>
                            <span>0.99</span>
                        </div>
                    </div>
                </div>

                <div class="settingsCard">
                    <div class="settingIconBadge">
                        <img src="../assets/authsetting_liveness.png" alt="">
                    </div>
                    <div class="settingLabel">
                        <span>ライブネス閾値</span>
                        <span class="helpIcon" title="写真や動画等によるなりすましではなく、実際に本人であることを判定するための閾値です。値を高くすると、なりすましを検知しやすくなりますが、本人の誤検知（誤認証）が増える可能性があります。">?</span>
                    </div>
                    <input
                        type="number"
                        class="settingNumberInput"
                        v-model.number="authSettings.livenessThreshold"
                        :disabled="authSettings.useRecommended"
                        min="0.40"
                        max="0.99"
                        step="0.01"
                    >
                    <div class="settingSliderArea">
                        <input
                            type="range"
                            class="settingSlider"
                            v-model.number="authSettings.livenessThreshold"
                            :style="sliderFillStyle(authSettings.livenessThreshold)"
                            :disabled="authSettings.useRecommended"
                            min="0.40"
                            max="0.99"
                            step="0.01"
                        >
                        <div class="settingRangeLabels">
                            <span>0.40</span>
                            <span>0.99</span>
                        </div>
                    </div>
                </div>

                <div class="settingsCard">
                    <div class="settingIconBadge">
                        <img src="../assets/authsetting_mask.png" class="maskIcon" alt="">
                    </div>
                    <div class="settingLabel">
                        <span>マスク時認証閾値</span>
                        <span class="helpIcon" title="マスクを着用した状態で顔認証を行う場合の類似度の閾値です。マスクで隠れる部分が多いため、顔認証閾値よりも低い値を設定することを推奨します。">?</span>
                    </div>
                    <input
                        type="number"
                        class="settingNumberInput"
                        v-model.number="authSettings.maskThreshold"
                        :disabled="authSettings.useRecommended"
                        min="0.40"
                        max="0.99"
                        step="0.01"
                    >
                    <div class="settingSliderArea">
                        <input
                            type="range"
                            class="settingSlider"
                            v-model.number="authSettings.maskThreshold"
                            :style="sliderFillStyle(authSettings.maskThreshold)"
                            :disabled="authSettings.useRecommended"
                            min="0.40"
                            max="0.99"
                            step="0.01"
                        >
                        <div class="settingRangeLabels">
                            <span>0.40</span>
                            <span>0.99</span>
                        </div>
                    </div>
                </div>

                <div class="settingsFooter">
                    <button type="button" class="clearButton" @click="resetAndSaveAuthSettings">デフォルトに戻す</button>
                    <button type="button" class="nextButton" @click="saveAuthSettings">変更を保存</button>
                </div>
            </div>
        </div>
        <div v-if="isLoading" class="loading-overlay">
            <div class="loading-box">
                <p>{{ loadingMessage }}</p>
                <div class="spinner"></div>
            </div>
        </div>
    </main>
</template>

<style scoped>
    .recommendedBanner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
        flex-wrap: wrap;
        background: #fdeaf4;
        border: 1px solid #f9c9e4;
        border-radius: 10px;
        padding: 14px 20px;
        margin: 0 20px 20px 20px;
    }
    .bannerText {
        display: flex;
        align-items: center;
        gap: 12px;
        text-align: left;
    }
    .infoIcon {
        width: 22px;
        height: 22px;
        object-fit: contain;
        flex-shrink: 0;
    }
    .bannerSwitchArea {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-shrink: 0;
    }
    .bannerSwitchLabel {
        font-weight: bold;
    }
    .helpIcon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 1px solid #bbb;
        color: #888;
        font-size: 12px;
        line-height: 1;
        cursor: pointer;
        flex-shrink: 0;
    }
    /* トグルスイッチ */
    .toggleSwitch {
        display: inline-flex;
        cursor: pointer;
    }
    .toggleSwitch input {
        display: none;
    }
    .toggleTrack {
        display: inline-block;
        width: 52px;
        height: 28px;
        background: #ccc;
        border-radius: 999px;
        position: relative;
        transition: 0.3s;
    }
    .toggleThumb {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 24px;
        height: 24px;
        background: white;
        border-radius: 50%;
        transition: 0.3s;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    }
    .toggleSwitch input:checked + .toggleTrack {
        background: #ff1493;
    }
    .toggleSwitch input:checked + .toggleTrack .toggleThumb {
        transform: translateX(24px);
    }
    /* 設定カード */
    .settingsCard {
        display: flex;
        align-items: center;
        gap: 24px;
        border: 1px solid #e0dede;
        border-radius: 10px;
        background: white;
        padding: 20px 24px;
        margin: 0 20px 16px 20px;
        flex-wrap: wrap;
    }
    .settingIconBadge {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
    .settingIconBadge img {
        width: 48px;
        height: 48px;
        object-fit: contain;
    }
    /* マスク画像は横長で余白が多く小さく見えるため拡大(レイアウト幅は他アイコンと揃える) */
    .settingIconBadge .maskIcon {
        transform: scale(1.4);
    }
    .settingLabel {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: bold;
        min-width: 240px;
        text-align: left;
    }
    .settingNumberInput {
        width: 145px;
        height: 46px;
        box-sizing: border-box;
        border: 1px solid #969292;
        border-radius: 6px;
        padding: 0 10px 0 16px;
        font-size: 18px;
        text-align: left;
    }
    /* 増減の矢印を常に表示する(Chrome/Edge/Safari) */
    .settingNumberInput::-webkit-inner-spin-button,
    .settingNumberInput::-webkit-outer-spin-button {
        opacity: 1;
    }
    .settingNumberInput:disabled {
        background: #f0f0f0;
        cursor: not-allowed;
    }
    .settingSliderArea {
        flex: 1;
        min-width: 220px;
        margin-left: 12px;
    }
    .settingSlider {
        --slider-color: #ff1493;
        -webkit-appearance: none;
        appearance: none;
        width: 100%;
        height: 6px;
        margin: 10px 0;
        border-radius: 3px;
        background: linear-gradient(to right, var(--slider-color) var(--fill, 0%), #e0dede var(--fill, 0%));
        outline: none;
        cursor: pointer;
    }
    /* つまみ: 白抜き・ピンク枠 */
    .settingSlider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 18px;
        height: 18px;
        box-sizing: border-box;
        border-radius: 50%;
        border: 3px solid var(--slider-color);
        background: white;
    }
    .settingSlider::-moz-range-thumb {
        width: 18px;
        height: 18px;
        box-sizing: border-box;
        border-radius: 50%;
        border: 3px solid var(--slider-color);
        background: white;
    }
    .settingSlider:disabled {
        --slider-color: #bbb;
        cursor: not-allowed;
    }
    .settingRangeLabels {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: #888;
        margin-top: 4px;
    }
    .settingsFooter {
        display: flex;
        justify-content: flex-end;
        gap: 20px;
        margin: 20px;
    }
    @media (max-width: 900px) {
        .settingsCard {
            flex-direction: column;
            align-items: stretch;
        }
        .settingLabel {
            min-width: 0;
        }
        .settingSliderArea {
            flex: 1;
            margin-left: 0;
        }
        .recommendedBanner {
            flex-direction: column;
            align-items: stretch;
        }
    }
    @media (max-width: 768px) {
        .mainView {
            margin-left: 0;
            margin-right: 0;
            padding: 10px;
        }
        .settingsFooter {
            flex-direction: column;
            margin: 10px;
        }
        .settingsFooter button {
            width: 100%;
        }
    }
</style>
